import type { LiveItem, PlayInfo } from '@module/apis/client-side/live/type';
import type useGetLoginInfoQuery from '@module/helpers/auth/useGetLoginInfoQuery';
import { adultWarningModalSafeLocalStorage } from '@module/lib/storage/storageImplementations';
import type { useFetchLivePlayMutation } from '@module/queries/live/liveQueryHooks';
import type { GoPlayParams } from '@module/types/play/goPlayTypes';
import TimeConstants from '@module/utils/item';
import type { PandatvPcModalAdapter } from '@pc/components/provider/ZustandModalAdapterProvider';

/**
 * 사이트 모드 관련 접근 권한
 * @link https://doublemedia.atlassian.net/wiki/spaces/INFOPandaT/pages/282198032/1.#0.-API-%EC%9A%94%EC%B2%AD-%EC%A0%84-%EC%B2%B4%ED%81%AC-%EA%B0%80%EC%9D%B4%EB%93%9C
 */
export const doesUserHaveSiteModeAccess = (
  memberLoginInfo: ReturnType<typeof useGetLoginInfoQuery>['loginInfo'],
  isAdult: LiveItem['isAdult'],
  onAuthConfirm: () => void,
  modalAdapter?: PandatvPcModalAdapter,
): boolean => {
  const siteMode = memberLoginInfo?.loginInfo.siteMode || { mode: 'c' };
  const { isLogin, authYN } = memberLoginInfo?.loginInfo.userInfo || { isLogin: false, authYN: 'N' };
  const isAuthDone = authYN === 'Y';

  const isTargetMode = siteMode.mode.includes('c') || siteMode.mode.includes('w');

  if (!isTargetMode || !isAdult) {
    return true;
  }

  if (!isLogin) {
    void modalAdapter?.showAuth();
    return false;
  }

  if (!isAuthDone) {
    const authMessage = '본인인증 후 시청이 가능합니다. \n 지금 본인인증 하시겠습니까?';

    void modalAdapter?.showConfirmCancel({ description: authMessage }).then((confirmed) => {
      if (confirmed) onAuthConfirm();
    });
    return false;
  }

  return true;
};

export const isPasswordProtectedStream = async (
  media: GoPlayParams,
  memberLoginInfo: ReturnType<typeof useGetLoginInfoQuery>['loginInfo'],
  goPlay: (
    media: GoPlayParams,
    mutateAsyncParams?: Partial<Parameters<ReturnType<typeof useFetchLivePlayMutation>['mutateAsync']>[0]>,
  ) => Promise<PlayInfo | undefined>,
  modalAdapter: PandatvPcModalAdapter | undefined,
): Promise<boolean> => {
  const isMyChannel = memberLoginInfo?.loginInfo.userInfo.id === media.userId;
  if (!media.isLive || isMyChannel || !media.isPw) {
    return false;
  }

  if (!modalAdapter) {
    return true;
  }

  void modalAdapter?.clear();

  const isLogin = memberLoginInfo?.loginInfo.userInfo.isLogin;
  if (!isLogin) {
    void modalAdapter?.showAuth();
    return true;
  }

  if (media.pw) {
    return false;
  }

  const { status, password } = await modalAdapter.showPasswordEntry();

  if (status === 'submit' && password) {
    goPlay({ ...media, isPw: true, pw: password });
  }

  return true;
};

export const checkAdultPopup = (media: GoPlayParams, modalAdapter?: PandatvPcModalAdapter) => {
  const adultWarningData = adultWarningModalSafeLocalStorage.get();
  const now = Date.now();
  const twentyFourHoursAgo = now - TimeConstants.ONE_DAY;

  const shouldShowModal =
    media.isAdult && (!adultWarningData?.checkedAt || adultWarningData.checkedAt < twentyFourHoursAgo);

  if (!shouldShowModal) {
    return true;
  }

  void modalAdapter?.showAdultWarning?.({ media });

  return false;
};

export const validatePasswordFormat = (password?: string | number, modalAdapter?: PandatvPcModalAdapter): boolean => {
  if (!password) return true;

  const stringifiedPw = String(password);
  const isValidLength = stringifiedPw.length >= 4 && stringifiedPw.length <= 15;

  if (isValidLength) {
    return true;
  }

  void modalAdapter?.showConfirm({ description: '비밀번호를 정확하게 입력해 주세요.' });

  return false;
};

export const checkForPaidContent = (
  type: GoPlayParams['type'],
  memberLoginInfo: ReturnType<typeof useGetLoginInfoQuery>['loginInfo'],
  modalAdapter?: PandatvPcModalAdapter,
) => {
  const isPaidContent = type === 'charge' || type === 'fan';
  if (!isPaidContent) return true;

  const isLoggedIn = memberLoginInfo?.loginInfo.userInfo.isLogin;
  if (isLoggedIn) return true;

  void modalAdapter?.showAuth();

  return false;
};
